/* eslint-disable jsx-a11y/img-redundant-alt */
/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  LinearProgress,
  Alert,
  FormControl,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  SvgIcon,
  useTheme,
  useMediaQuery,
  CircularProgress,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  MenuItem,
} from '@mui/material';
import {
  Send as SendIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { complianceService } from '../../../app/services/api/compliance/complianceService';
import { Audit, AuditSection, AuditQuestion, AuditResponse, ActionTrigger, ActionTriggerPhotoGroup, ActionTriggerSignatureGroup, ActionItemDetails } from '../../../pages/Compliance/Interfaces/complicanceInterfaces';
import { QuestionType, ActionTriggerType, TriggerCondition, AuditStatus } from '../../../pages/Compliance/Enums/complianceEnums';
import PageArea from '../../../components/shared/PageArea';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';
import PageTitle from '../../../components/shared/PageTitle';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../components/shared/useUser';
import LeavePageModal from '../../../components/shared/dialogs/LeavePageModal';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { Section } from '../../../components/ui/Section';
import { resizeImageForUpload } from '../../../utils/imageUtils';

const AuditExecutionPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { auditId } = useParams<{ auditId: string }>();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { getCurrentUser } = useNswagClient();
  const { t } = useTranslation('complianceAudits');
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  // Permission checks for execution
  const canExecuteAudit = hasPermissionTo(['AuditsExecute']);
  const canUploadPhotos = hasPermissionTo(['AuditPhotoUpload']);
  const canESign = hasPermissionTo(['AuditsESign']);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [audit, setAudit] = useState<Audit | null>(null);
  const [sections, setSections] = useState<AuditSection[]>([]);
  const [responses, setResponses] = useState<Record<number, AuditResponse>>({});
  const [currentScore, setCurrentScore] = useState(0);
  const [maxScore, setMaxScore] = useState(0);

  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [currentPhotoQuestionId, setCurrentPhotoQuestionId] = useState<number | null>(null);
  const [currentSignatureQuestionId, setCurrentSignatureQuestionId] = useState<number | null>(null);
  const [isActionTriggerSignature, setIsActionTriggerSignature] = useState(false);
  const [isActionTriggerPhoto, setIsActionTriggerPhoto] = useState(false);
  const [auditorName, setAuditorName] = useState('');
  const [auditorSignature, setAuditorSignature] = useState('');
  const [triggeredActions, setTriggeredActions] = useState<Record<number, ActionTrigger[]>>({});
  // Per-trigger notes: keyed by "questionId-triggerIdx" so each RequireNote trigger gets its own note box
  const [triggerNotes, setTriggerNotes] = useState<Record<string, string>>({});
  // Per-trigger photos: keyed by "questionId-triggerIdx" so each RequirePhoto trigger gets its own photo set
  const [triggerPhotos, setTriggerPhotos] = useState<Record<string, string[]>>({});
  // Per-trigger signatures: keyed by "questionId-triggerIdx" so each RequireSignature trigger gets its own signature
  const [triggerSignatures, setTriggerSignatures] = useState<Record<string, string>>({});
  // Track which trigger index opened the camera/signature dialog
  const [currentPhotoTriggerIdx, setCurrentPhotoTriggerIdx] = useState<number | null>(null);
  const [currentSignatureTriggerIdx, setCurrentSignatureTriggerIdx] = useState<number | null>(null);
  // Action item drafts: keyed by "questionId-triggerIdx"
  const [actionItemDrafts, setActionItemDrafts] = useState<Record<string, ActionItemDetails>>({});
  const [actionItemDialogOpen, setActionItemDialogOpen] = useState(false);
  const [currentActionItemKey, setCurrentActionItemKey] = useState<string | null>(null);
  const [currentActionItemTrigger, setCurrentActionItemTrigger] = useState<ActionTrigger | null>(null);
  // Autosave state
  const [modifiedQuestions, setModifiedQuestions] = useState<Set<number>>(new Set());
  const [showAutosaveMessage, setShowAutosaveMessage] = useState<boolean>(false);
  const [autosaving, setAutosaving] = useState(false);

  const responsesRef = useRef(responses);
  const auditRef = useRef(audit);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const savingRef = useRef(false);

  useEffect(() => { responsesRef.current = responses; }, [responses]);
  useEffect(() => { auditRef.current = audit; }, [audit]);

  const AUTOSAVE_DEBOUNCE = 1500;

  const blocker = useBlocker(() => modifiedQuestions.size > 0);

  const saveNow = useCallback(async () => {
    if (!auditRef.current || savingRef.current) return;

    if (auditRef.current.status === AuditStatus.Submitted) return;
    savingRef.current = true;
    setAutosaving(true);
    try {
      const responsesList = Object.values(responsesRef.current);
      await complianceService.audits.updateResponses(auditRef.current.id!, {
        storeNumber: selectedStore?.storeNumber,
        responses: responsesList,
      }, selectedStore?.storeNumber);
      setShowAutosaveMessage(true);
      setModifiedQuestions(new Set());
    } catch (err) {
      console.error('Autosave failed:', err);
    } finally {
      savingRef.current = false;
      setAutosaving(false);
    }
  }, [selectedStore?.storeNumber]);

  const debouncedSave = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      saveNow();
    }, AUTOSAVE_DEBOUNCE);
  }, [saveNow]);

  const dataUrlToBlob = (dataUrl: string): Blob => {
    const [header, base64] = dataUrl.split(',');
    const contentType = header.match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bytes = atob(base64);
    const byteArray = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) {
      byteArray[i] = bytes.charCodeAt(i);
    }
    return new Blob([byteArray], { type: contentType });
  };

  const uploadToBlob = async (
    questionId: number,
    fileOrDataUrl: File | string,
  ): Promise<string> => {
    const blob = typeof fileOrDataUrl === 'string'
      ? dataUrlToBlob(fileOrDataUrl)
      : fileOrDataUrl;
    const resized = await resizeImageForUpload(blob);
    return complianceService.audits.uploadPhoto(
      audit?.id || parseInt(auditId || '0'),
      questionId,
      resized,
      selectedStore?.storeNumber,
    );
  };

  const syncTriggerMediaToResponse = (
    questionId: number,
    updatedPhotos: Record<string, string[]>,
    updatedSigs: Record<string, string>,
    currentTriggeredActions?: Record<number, ActionTrigger[]>,
  ) => {
    const triggers = (currentTriggeredActions || triggeredActions)[questionId] || [];

    const triggerPhotoGroups: ActionTriggerPhotoGroup[] = [];
    triggers.forEach((trigger, idx) => {
      if (trigger.triggerType === ActionTriggerType.RequirePhoto) {
        const photos = updatedPhotos[`${questionId}-${idx}`] || [];
        if (photos.length > 0) {
          triggerPhotoGroups.push({ triggerIndex: idx, photoUrls: photos });
        }
      }
    });

    const triggerSigGroups: ActionTriggerSignatureGroup[] = [];
    triggers.forEach((trigger, idx) => {
      if (trigger.triggerType === ActionTriggerType.RequireSignature) {
        const sig = updatedSigs[`${questionId}-${idx}`];
        if (sig) {
          triggerSigGroups.push({ triggerIndex: idx, signatureUrl: sig });
        }
      }
    });

    const response = responsesRef.current[questionId];
    const newResponse = {
      ...response,
      actionPhotoUrls: [],
      actionSignatureUrl: undefined,
      actionTriggerPhotos: triggerPhotoGroups.length > 0 ? triggerPhotoGroups : undefined,
      actionTriggerSignatures: triggerSigGroups.length > 0 ? triggerSigGroups : undefined,
    };
    const newResponses = { ...responsesRef.current, [questionId]: newResponse };
    setResponses(newResponses);
    responsesRef.current = newResponses;
    setModifiedQuestions(prev => new Set(prev).add(questionId));
    setShowAutosaveMessage(false);
    debouncedSave();
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleLeavePageModalCancel = () => {
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };

  const handleLeavePageModalClose = () => {
    if (blocker?.reset) {
      blocker.reset();
    }
  };

  const handleLeavePageModalConfirm = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    saveNow();
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event: { preventDefault: () => void; returnValue: string }) => {
      if (modifiedQuestions.size > 0) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [modifiedQuestions]);

  useEffect(() => {
    loadAudit();
    loadCurrentUser();
  }, [auditId, selectedStore?.storeNumber]);

  const loadCurrentUser = async () => {
    try {
      const userResponse = await getCurrentUser();
      if (userResponse.data) {
        setAuditorName(userResponse.data.emailAddress || t('execution.unknownUser'));
      }
    } catch (err) {
      console.error('Failed to load current user:', err);
    }
  };

  const loadAudit = async () => {
    if (!auditId) {
      setError(t('execution.auditIdRequired'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const auditData = await complianceService.audits.getById(parseInt(auditId), selectedStore?.storeNumber);

      if (auditData.status !== AuditStatus.Submitted
        && auditData.scheduledDate && new Date(auditData.scheduledDate) > new Date()) {
        setError(t('execution.notYetAvailable'));
        setLoading(false);
        return;
      }

      setAudit(auditData);

      let sectionsData: AuditSection[] = [];

      const templateData = auditData.template
        ?? (auditData.templateId && hasPermissionTo(['AuditTemplatesView'])
          ? await complianceService.auditTemplates.getById(auditData.templateId, selectedStore?.storeNumber)
          : null);

      if (templateData) {
        sectionsData = (templateData.sections || []).map(templateSection => ({
          id: templateSection.id,
          auditTemplateId: templateSection.auditTemplateId,
          auditId: auditData.id,
          name: templateSection.name,
          description: templateSection.description,
          displayOrder: templateSection.displayOrder,
          questions: (templateSection.questions || []).map(templateQuestion => ({
            id: templateQuestion.id,
            sectionId: templateSection.id,
            questionText: templateQuestion.questionText,
            questionType: templateQuestion.questionType,
            isRequired: templateQuestion.isRequired,
            maxScore: templateQuestion.maxScore,
            displayOrder: templateQuestion.displayOrder,
            helpText: templateQuestion.helpText,
            options: templateQuestion.options || [],
            actionTriggers: templateQuestion.actionTriggers || [],
          })).sort((a, b) => a.displayOrder - b.displayOrder),
        })).sort((a, b) => a.displayOrder - b.displayOrder);

        setSections(sectionsData);

        const total = sectionsData.reduce((sum: number, section: AuditSection) => {
          return sum + (section.questions?.reduce((qSum: number, q: AuditQuestion) => qSum + (q.maxScore || 0), 0) || 0);
        }, 0);
        setMaxScore(total);
      }

      const responsesData = auditData.responses || [];
      const responsesMap: Record<number, AuditResponse> = {};
      responsesData.forEach(r => {
        if (r.questionId) {
          responsesMap[r.questionId] = r;
        }
      });
      setResponses(responsesMap);

      calculateScore(responsesMap, sectionsData);

      if (auditData.firstSignatureData) {
        setAuditorSignature(auditData.firstSignatureData);
      }

      const loadedTriggers: Record<number, ActionTrigger[]> = {};
      sectionsData.forEach(section => {
        section.questions?.forEach(question => {
          const response = responsesMap[question.id!];
          if (response) {
            let responseValue: string | number | boolean | null = null;
            switch (question.questionType) {
              case QuestionType.Boolean:
                responseValue = response.responseBoolean ?? null;
                break;
              case QuestionType.Number:
              case QuestionType.Rating:
              case QuestionType.SingleChoice:
                responseValue = response.responseNumber ?? null;
                break;
              case QuestionType.MultipleChoice:
              case QuestionType.Text:
              case QuestionType.Date:
                responseValue = response.responseText || null;
                break;
              case QuestionType.Photo:
                responseValue = (response.photoUrls && response.photoUrls.length > 0)
                  ? response.responseText || 'photo'
                  : null;
                break;
              case QuestionType.Signature:
                responseValue = response.signatureUrl || null;
                break;
              default:
                break;
            }
            if (responseValue !== null) {
              const activated = checkActionTriggers(question, responseValue);
              if (activated.length > 0) {
                loadedTriggers[question.id!] = activated;
              }
            }

            if (!loadedTriggers[question.id!] && question.actionTriggers?.length) {
              const hasActionData = response.actionSignatureUrl
                || response.actionNote
                || (response.actionPhotoUrls && response.actionPhotoUrls.length > 0);
              if (hasActionData) {
                loadedTriggers[question.id!] = question.actionTriggers;
              }
            }
          }
        });
      });
      setTriggeredActions(loadedTriggers);

      // Populate per-trigger note slots from existing actionNote data
      const initialTriggerNotes: Record<string, string> = {};
      for (const [qIdStr, triggers] of Object.entries(loadedTriggers)) {
        const qId = parseInt(qIdStr);
        const resp = responsesMap[qId];
        if (!resp?.actionNote) continue;
        const noteTriggersCount = triggers.filter(t => t.triggerType === ActionTriggerType.RequireNote).length;
        if (noteTriggersCount <= 1) {
          // Single note trigger — put the whole note in its slot
          const idx = triggers.findIndex(t => t.triggerType === ActionTriggerType.RequireNote);
          if (idx >= 0) initialTriggerNotes[`${qId}-${idx}`] = resp.actionNote;
        } else {
          // Multiple note triggers — try to split by separator, fall back to first slot
          const parts = resp.actionNote.split('\n---\n');
          let partIdx = 0;
          triggers.forEach((trig, idx) => {
            if (trig.triggerType === ActionTriggerType.RequireNote) {
              initialTriggerNotes[`${qId}-${idx}`] = parts[partIdx] || '';
              partIdx++;
            }
          });
        }
      }
      setTriggerNotes(initialTriggerNotes);

      const initialTriggerPhotos: Record<string, string[]> = {};
      const initialTriggerSignatures: Record<string, string> = {};
      for (const [qIdStr, trigs] of Object.entries(loadedTriggers)) {
        const qId = parseInt(qIdStr);
        const resp = responsesMap[qId];

        if (resp?.actionTriggerPhotos && resp.actionTriggerPhotos.length > 0) {
          for (const group of resp.actionTriggerPhotos) {
            if (group.photoUrls.length > 0) {
              initialTriggerPhotos[`${qId}-${group.triggerIndex}`] = [...group.photoUrls];
            }
          }
        } else if (resp?.actionPhotoUrls && resp.actionPhotoUrls.length > 0) {
          const firstPhotoTriggerIdx = trigs.findIndex(t => t.triggerType === ActionTriggerType.RequirePhoto);
          if (firstPhotoTriggerIdx >= 0) {
            initialTriggerPhotos[`${qId}-${firstPhotoTriggerIdx}`] = [...resp.actionPhotoUrls];
          }
        }

        if (resp?.actionTriggerSignatures && resp.actionTriggerSignatures.length > 0) {
          for (const group of resp.actionTriggerSignatures) {
            if (group.signatureUrl) {
              initialTriggerSignatures[`${qId}-${group.triggerIndex}`] = group.signatureUrl;
            }
          }
        } else if (resp?.actionSignatureUrl) {
          const firstSigTriggerIdx = trigs.findIndex(t => t.triggerType === ActionTriggerType.RequireSignature);
          if (firstSigTriggerIdx >= 0) {
            initialTriggerSignatures[`${qId}-${firstSigTriggerIdx}`] = resp.actionSignatureUrl;
          }
        }
      }
      setTriggerPhotos(initialTriggerPhotos);
      setTriggerSignatures(initialTriggerSignatures);

      const initialActionItemDrafts: Record<string, ActionItemDetails> = {};
      for (const [qIdStr, trigs] of Object.entries(loadedTriggers)) {
        const qId = parseInt(qIdStr);
        const resp = responsesMap[qId];
        if (resp?.actionItemDetails) {
          const aiTriggerIdx = trigs.findIndex(t => t.triggerType === ActionTriggerType.CreateActionItem);
          if (aiTriggerIdx >= 0) {
            initialActionItemDrafts[`${qId}-${aiTriggerIdx}`] = {
              title: resp.actionItemDetails.title || '',
              description: resp.actionItemDetails.description || '',
              priority: resp.actionItemDetails.priority || 'Medium',
              dueDate: resp.actionItemDetails.dueDate || null,
              active: resp.actionItemDetails.active ?? false,
            };
          }
        }
      }
      setActionItemDrafts(initialActionItemDrafts);

      setModifiedQuestions(new Set());
      setShowAutosaveMessage(false);


    } catch (err) {
      setError(err instanceof Error ? err.message : t('execution.failedToLoadAudit'));
    } finally {
      setLoading(false);
    }
  };

  const calculateScore = (responsesMap: Record<number, AuditResponse>, sectionsList?: AuditSection[]) => {
    let score = 0;
    let max = 0;
    (sectionsList || sections).forEach(section => {
      section.questions?.forEach(question => {
        const response = responsesMap[question.id!];
        if (response?.score != null) {
          score += response.score;
          max += question.maxScore || 0;
        }
      });
    });
    setCurrentScore(score);
    setMaxScore(max);
  };

  const checkActionTriggers = (question: AuditQuestion, value: string | number | boolean): ActionTrigger[] => {
    if (!question.actionTriggers || question.actionTriggers.length === 0) {
      return [];
    }

    const activatedTriggers: ActionTrigger[] = [];

    // Normalize a value to boolean for Boolean-type questions
    const toBool = (v: string | number | boolean): boolean | null => {
      if (typeof v === 'boolean') return v;
      if (typeof v === 'number') return v !== 0;
      const s = String(v).toLowerCase().trim();
      if (s === 'true' || s === 'yes' || s === '1') return true;
      if (s === 'false' || s === 'no' || s === '0') return false;
      return null;
    };

    // Type-aware comparison between response value and trigger value
    const valuesMatch = (triggerVal: string | number | boolean): boolean => {
      // Boolean questions: normalise both sides to boolean
      if (question.questionType === QuestionType.Boolean) {
        const a = toBool(value);
        const b = toBool(triggerVal);
        return a !== null && b !== null && a === b;
      }

      // SingleChoice: compare by option ID or option text
      if (question.questionType === QuestionType.SingleChoice) {
        const numVal = Number(value);
        const numTrigger = Number(triggerVal);
        if (!isNaN(numVal) && !isNaN(numTrigger) && numVal === numTrigger) return true;
        // Also match when triggerValue is the option's display text
        if (!isNaN(numVal)) {
          const selectedOption = question.options?.find(o => o.id === numVal);
          if (selectedOption && String(triggerVal).toLowerCase().trim() === selectedOption.optionText.toLowerCase().trim()) return true;
        }
        return false;
      }

      // MultipleChoice: value is comma-separated option IDs; triggerValue is option text or ID
      if (question.questionType === QuestionType.MultipleChoice) {
        const selectedIds = String(value).split(',').filter(Boolean).map(id => parseInt(id));
        const triggerStr = String(triggerVal).toLowerCase().trim();
        // Check if any selected option's text or ID matches the trigger value
        return selectedIds.some(optId => {
          if (String(optId) === triggerStr) return true;
          const opt = question.options?.find(o => o.id === optId);
          return opt ? opt.optionText.toLowerCase().trim() === triggerStr : false;
        });
      }

      // Number / Rating: numeric comparison
      if (question.questionType === QuestionType.Number || question.questionType === QuestionType.Rating) {
        const a = Number(value);
        const b = Number(triggerVal);
        return !isNaN(a) && !isNaN(b) && a === b;
      }

      // Default (Text, Date, etc.): case-insensitive string comparison
      return String(value).toLowerCase().trim() === String(triggerVal).toLowerCase().trim();
    };

    question.actionTriggers.forEach(trigger => {
      let isTriggered = false;

      switch (trigger.triggerCondition) {
        case TriggerCondition.Equals:
          isTriggered = valuesMatch(trigger.triggerValue);
          break;
        case TriggerCondition.NotEquals:
          isTriggered = !valuesMatch(trigger.triggerValue);
          break;
        case TriggerCondition.LessThan:
          isTriggered = Number(value) < Number(trigger.triggerValue);
          break;
        case TriggerCondition.GreaterThan:
          isTriggered = Number(value) > Number(trigger.triggerValue);
          break;
        case TriggerCondition.Contains:
          isTriggered = String(value).toLowerCase().includes(String(trigger.triggerValue).toLowerCase());
          break;
      }

      if (isTriggered) {
        activatedTriggers.push(trigger);
      }
    });

    return activatedTriggers;
  };

  const handleResponseChange = (questionId: number, question: AuditQuestion, value: string | number | boolean) => {
    const scoreEarned = calculateQuestionScore(question, value);

    // Check for action triggers
    const activatedTriggers = checkActionTriggers(question, value);
    if (activatedTriggers.length > 0) {
      setTriggeredActions({ ...triggeredActions, [questionId]: activatedTriggers });
    } else {
      // Remove triggers if answer changed to not trigger actions
      const newTriggered = { ...triggeredActions };
      delete newTriggered[questionId];
      setTriggeredActions(newTriggered);
    }

    // Prepare selectedOptionIds for choice-based questions
    let selectedOptionIds: number[] = [];
    if (question.questionType === QuestionType.SingleChoice && typeof value === 'number') {
      selectedOptionIds = [value];
    } else if (question.questionType === QuestionType.MultipleChoice && typeof value === 'string') {
      selectedOptionIds = value.split(',').filter(Boolean).map(id => parseInt(id));
    }

    const newResponse: AuditResponse = {
      ...responses[questionId],
      id: responses[questionId]?.id || 0,
      questionId,
      auditId: audit?.id || 0,
      responseText: typeof value === 'string' ? value : null,
      responseNumber: typeof value === 'number' ? value : null,
      responseBoolean: typeof value === 'boolean' ? value : null,
      selectedOptionIds,
      photoUrls: responses[questionId]?.photoUrls || [],
      signatureUrl: responses[questionId]?.signatureUrl || null,
      score: scoreEarned,
    };

    const newResponses = { ...responses, [questionId]: newResponse };
    setResponses(newResponses);
    calculateScore(newResponses);

    // Track modified question and reset save message
    setModifiedQuestions(prev => new Set(prev).add(questionId));
    setShowAutosaveMessage(false);
    debouncedSave();
  };

  const handlePhotoCapture = async (questionId: number, question: AuditQuestion, files: FileList) => {
    const existingPhotos = responses[questionId]?.photoUrls || [];
    const remainingSlots = 3 - existingPhotos.length;

    if (remainingSlots <= 0) {
      setError(t('execution.maxPhotos'));
      setTimeout(() => setError(null), 3000);
      return;
    }

    const filesToAdd = Array.from(files).slice(0, remainingSlots);

    try {
      setUploading(true);
      const uploadPromises = filesToAdd.map(file => uploadToBlob(questionId, file));
      const uploadedUrls = await Promise.all(uploadPromises);

      const allPhotoUrls = [...existingPhotos, ...uploadedUrls];

      const newResponse: AuditResponse = {
        ...responses[questionId],
        id: responses[questionId]?.id || 0,
        questionId,
        auditId: audit?.id || 0,
        responseText: t('execution.photoCount', { count: allPhotoUrls.length }),
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: allPhotoUrls,
        signatureUrl: null,
        score: question.maxScore || 0,
      };

      const newResponses = { ...responses, [questionId]: newResponse };
      setResponses(newResponses);
      calculateScore(newResponses);

      setModifiedQuestions(prev => new Set(prev).add(questionId));
      setShowAutosaveMessage(false);
      debouncedSave();
    } catch {
      setError(t('execution.photoProcessFailed'));
      setTimeout(() => setError(null), 3000);
    } finally {
      setUploading(false);
    }
  };

  const handleCameraCapture = async (questionId: number, question: AuditQuestion, photoDataUrl: string) => {
    try {
      setUploading(true);
      const uploadedUrl = await uploadToBlob(questionId, photoDataUrl);

      const existingPhotos = responses[questionId]?.photoUrls || [];
      const allPhotoUrls = [...existingPhotos, uploadedUrl];

      const newResponse: AuditResponse = {
        ...responses[questionId],
        id: responses[questionId]?.id || 0,
        questionId,
        auditId: audit?.id || 0,
        responseText: t('execution.photoCount', { count: allPhotoUrls.length }),
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: allPhotoUrls,
        signatureUrl: null,
        score: question.maxScore || 0,
      };

      const newResponses = { ...responses, [questionId]: newResponse };
      setResponses(newResponses);
      calculateScore(newResponses);

      setModifiedQuestions(prev => new Set(prev).add(questionId));
      setShowAutosaveMessage(false);
      debouncedSave();
    } catch {
      setError(t('execution.photoProcessFailed'));
      setTimeout(() => setError(null), 3000);
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoRemove = (questionId: number, photoIndex: number) => {
    const response = responses[questionId];
    if (!response?.photoUrls?.[photoIndex]) return;

    const newPhotoUrls = [...(response?.photoUrls || [])];
    newPhotoUrls.splice(photoIndex, 1);

    const newResponse: AuditResponse = {
      ...response,
      id: response?.id || 0,
      questionId,
      auditId: audit?.id || 0,
      responseText: newPhotoUrls.length > 0 ? t('execution.photoCount', { count: newPhotoUrls.length }) : null,
      responseNumber: null,
      responseBoolean: null,
      selectedOptionIds: [],
      photoUrls: newPhotoUrls,
      signatureUrl: null,
      score: newPhotoUrls.length > 0 ? (response?.score || 0) : 0,
    };

    const newResponses = { ...responses, [questionId]: newResponse };
    setResponses(newResponses);
    calculateScore(newResponses);

    setModifiedQuestions(prev => new Set(prev).add(questionId));
    setShowAutosaveMessage(false);
    debouncedSave();
  };

  const handleSignatureCapture = async (questionId: number, question: AuditQuestion, signatureDataUrl: string) => {
    try {
      setUploading(true);
      const uploadedUrl = await uploadToBlob(questionId, signatureDataUrl);

      const newResponse: AuditResponse = {
        ...responses[questionId],
        id: responses[questionId]?.id || 0,
        questionId,
        auditId: audit?.id || 0,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: uploadedUrl,
        score: question.maxScore || 0,
      };

      const newResponses = { ...responses, [questionId]: newResponse };
      setResponses(newResponses);
      calculateScore(newResponses);
      setSignatureDialogOpen(false);
      setCurrentSignatureQuestionId(null);

      setModifiedQuestions(prev => new Set(prev).add(questionId));
      setShowAutosaveMessage(false);
      debouncedSave();
    } catch {
      setError(t('execution.photoProcessFailed'));
      setTimeout(() => setError(null), 3000);
    } finally {
      setUploading(false);
    }
  };

  const handleSignatureRemove = (questionId: number) => {
    const newResponse: AuditResponse = {
      ...responses[questionId],
      id: responses[questionId]?.id || 0,
      questionId,
      auditId: audit?.id || 0,
      responseText: null,
      responseNumber: null,
      responseBoolean: null,
      selectedOptionIds: [],
      photoUrls: [],
      signatureUrl: null,
      score: 0,
    };

    const newResponses = { ...responses, [questionId]: newResponse };
    setResponses(newResponses);
    calculateScore(newResponses);

    // Trigger autosave
    setModifiedQuestions(prev => new Set(prev).add(questionId));
    setShowAutosaveMessage(false);
    debouncedSave();
  };

  const isNAOption = (optionText: string): boolean => {
    const normalized = optionText.toLowerCase().trim();
    return normalized === 'n/a' || normalized === 'not applicable';
  };

  const clampScore = (score: number, maxScore: number | null): number => {
    const max = maxScore || 0;
    return Math.max(0, Math.min(score, max));
  };

  const calculateQuestionScore = (question: AuditQuestion, value: string | number | boolean): number | null => {
    if (value === '' || value === null || value === undefined) return 0;

    switch (question.questionType) {
      case QuestionType.Boolean:
        return value === true ? (question.maxScore || 0) : 0;

      case QuestionType.SingleChoice: {
        const optionId = typeof value === 'number' ? value : parseInt(String(value));
        const option = question.options?.find(o => o.id === optionId);
        if (!option) return 0;
        if (isNAOption(option.optionText)) return null;
        return clampScore(option.score || 0, question.maxScore);
      }

      case QuestionType.MultipleChoice: {
        if (typeof value === 'string') {
          const selectedIds = value.split(',').filter(Boolean).map(id => parseInt(id));
          const selectedOptions = selectedIds.map(id => question.options?.find(o => o.id === id)).filter(Boolean);
          if (selectedOptions.some(opt => isNAOption(opt!.optionText))) return null;
          const rawScore = selectedOptions.reduce((sum, opt) => sum + (opt?.score || 0), 0);
          return clampScore(rawScore, question.maxScore);
        }
        return 0;
      }

      case QuestionType.Number: {
        const numValue = typeof value === 'number' ? value : parseFloat(String(value));
        if (isNaN(numValue)) return 0;
        return question.maxScore || 0;
      }

      case QuestionType.Rating: {
        const ratingValue = typeof value === 'number' ? value : parseInt(String(value));
        if (isNaN(ratingValue)) return 0;
        const rawScore = Math.round(((ratingValue / 5) * (question.maxScore || 0)));
        return clampScore(rawScore, question.maxScore);
      }

      case QuestionType.Text:
      case QuestionType.Date:
      case QuestionType.Photo:
      case QuestionType.Signature:
        return question.maxScore || 0;
      default:
        return 0;
    }
  };

  const isQuestionAnswered = (question: AuditQuestion): boolean => {
    const response = responses[question.id!];
    return !!(response && (
      response.responseText ||
      response.responseNumber !== null ||
      response.responseBoolean !== null ||
      (response.selectedOptionIds && response.selectedOptionIds.length > 0) ||
      (response.photoUrls && response.photoUrls.length > 0) ||
      response.signatureUrl
    ));
  };

  const isSectionComplete = (section: AuditSection): boolean => {
    if (!section.questions || section.questions.length === 0) return true;

    const allRequiredAnswered = section.questions
      .filter(q => q.isRequired)
      .every(q => isQuestionAnswered(q));
    if (!allRequiredAnswered) return false;

    for (const question of section.questions) {
      const triggers = triggeredActions[question.id!];
      if (!triggers) continue;
      for (let triggerIdx = 0; triggerIdx < triggers.length; triggerIdx++) {
        const trigger = triggers[triggerIdx];
        if (!trigger.isRequired) continue;
        const key = `${question.id!}-${triggerIdx}`;
        if (trigger.triggerType === ActionTriggerType.RequireNote && !triggerNotes[key]?.trim()) {
          return false;
        }
        if (trigger.triggerType === ActionTriggerType.RequirePhoto && (!triggerPhotos[key] || triggerPhotos[key].length === 0)) {
          return false;
        }
        if (trigger.triggerType === ActionTriggerType.RequireSignature && !triggerSignatures[key]) {
          return false;
        }
      }
    }

    return true;
  };

  const openActionItemDialog = (questionId: number, triggerIdx: number, trigger: ActionTrigger) => {
    const key = `${questionId}-${triggerIdx}`;
    if (!actionItemDrafts[key]) {
      // Pre-populate from trigger config
      const dueDate = trigger.actionItemDueWeeks
        ? new Date(Date.now() + trigger.actionItemDueWeeks * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        : null;
      setActionItemDrafts(prev => ({
        ...prev,
        [key]: {
          title: trigger.actionItemTitle || '',
          description: trigger.actionItemDescription || '',
          priority: trigger.actionItemPriority || 'Medium',
          dueDate,
          active: false,
        },
      }));
    }
    setCurrentActionItemKey(key);
    setCurrentActionItemTrigger(trigger);
    setActionItemDialogOpen(true);
  };

  const saveActionItemDraft = () => {
    if (!currentActionItemKey) return;
    const draft = actionItemDrafts[currentActionItemKey];
    if (!draft?.title?.trim() || !draft?.description?.trim()) return;

    const draftWithInactive = { ...draft, active: false };
    const questionId = parseInt(currentActionItemKey.split('-')[0]);
    setResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        actionItemDetails: draftWithInactive,
      },
    }));

    setModifiedQuestions(prev => new Set(prev).add(questionId));
    setShowAutosaveMessage(false);
    debouncedSave();
    setActionItemDialogOpen(false);
    setCurrentActionItemKey(null);
    setCurrentActionItemTrigger(null);
  };

  const handleSubmit = async () => {
    if (!auditorSignature) {
      setError(t('execution.signatureRequiredToSubmit'));
      return;
    }

    // Validate all required questions are answered
    const unansweredRequired: string[] = [];
    sections.forEach(section => {
      section.questions?.forEach(question => {
        if (question.isRequired && !isQuestionAnswered(question)) {
          unansweredRequired.push(question.questionText);
        }
      });
    });

    if (unansweredRequired.length > 0) {
      setError(t('execution.requiredQuestionsUnansweredList', {
        questions: unansweredRequired.join(', '),
      }));
      return;
    }

    // Validate required action triggers (note, photo, signature)
    for (const [questionIdStr, triggers] of Object.entries(triggeredActions)) {
      const questionId = parseInt(questionIdStr);

      for (const trigger of triggers) {
        if (trigger.isRequired) {
          if (trigger.triggerType === ActionTriggerType.RequireNote) {
            const triggerIdx = triggers.indexOf(trigger);
            const noteKey = `${questionId}-${triggerIdx}`;
            if (!triggerNotes[noteKey]?.trim()) {
              setError(t('execution.requiredActionNotesMissing'));
              return;
            }
          }
          if (trigger.triggerType === ActionTriggerType.RequirePhoto) {
            const triggerIdx = triggers.indexOf(trigger);
            const photoKey = `${questionId}-${triggerIdx}`;
            if (!triggerPhotos[photoKey] || triggerPhotos[photoKey].length === 0) {
              setError(t('execution.requiredActionPhotosMissing'));
              return;
            }
          }
          if (trigger.triggerType === ActionTriggerType.RequireSignature) {
            const triggerIdx = triggers.indexOf(trigger);
            const sigKey = `${questionId}-${triggerIdx}`;
            if (!triggerSignatures[sigKey]) {
              setError(t('execution.requiredActionSignatureMissing'));
              return;
            }
          }
          if (trigger.triggerType === ActionTriggerType.CreateActionItem) {
            const triggerIdx = triggers.indexOf(trigger);
            const aiKey = `${questionId}-${triggerIdx}`;
            const draft = actionItemDrafts[aiKey];
            if (!draft?.title?.trim() || !draft?.description?.trim()) {
              setError(t('execution.actionItemDetailsMissing'));
              return;
            }
          }
        }
      }
    }

    try {
      setSaving(true);
      setError(null);

      await complianceService.audits.submit(audit!.id!, {
        storeNumber: selectedStore?.storeNumber,
        comments: '',
        signatureData: auditorSignature,
      }, selectedStore?.storeNumber);

      setSuccess(t('execution.submitSuccess'));
      setModifiedQuestions(new Set());
      setTimeout(() => {
        navigate('/compliance/audits/history');
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('execution.submitFailed'));
    } finally {
      setSaving(false);
      setSubmitDialogOpen(false);
    }
  };



  const renderQuestion = (question: AuditQuestion) => {
    const response = responses[question.id!];

    switch (question.questionType) {
      case QuestionType.Text:
        return (
          <TextField
            fullWidth
            multiline
            sx={{ mb: 0 }}
            rows={6}
            value={response?.responseText || ''}
            onChange={(e) => handleResponseChange(question.id!, question, e.target.value)}
            placeholder={t('execution.enterResponse')}
            disabled={isReadOnly}
          />
        );

      case QuestionType.Number:
        return (
          <TextField
            fullWidth
            sx={{ mb: 0 }}
            type="number"
            value={response?.responseNumber || ''}
            onChange={(e) => handleResponseChange(question.id!, question, parseFloat(e.target.value))}
            placeholder={t('execution.enterNumber')}
            disabled={isReadOnly}
          />
        );

      case QuestionType.Boolean:
        return (
          <FormControl component="fieldset">
            <FormControlLabel
              control={
                <Checkbox
                  checked={response?.responseBoolean === true}
                  disabled={isReadOnly}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleResponseChange(question.id!, question, true);
                    } else {
                      handleResponseChange(question.id!, question, '');
                    }
                  }}
                />
              }
              label={t('common.yes')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={response?.responseBoolean === false}
                  disabled={isReadOnly}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleResponseChange(question.id!, question, false);
                    } else {
                      handleResponseChange(question.id!, question, '');
                    }
                  }}
                />
              }
              label={t('common.no')}
            />
          </FormControl>
        );

      case QuestionType.SingleChoice:
        return (
          <FormControl component="fieldset">
            {question.options?.map(option => (
              <FormControlLabel
                key={option.id}
                control={
                  <Checkbox
                    checked={response?.responseNumber?.toString() === option.id?.toString()}
                    disabled={isReadOnly}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleResponseChange(question.id!, question, option.id!);
                      } else {
                        handleResponseChange(question.id!, question, '');
                      }
                    }}
                  />
                }
                label={option.optionText}
              />
            ))}
          </FormControl>
        );

      case QuestionType.MultipleChoice:
        return (
          <FormControl component="fieldset">
            <Box sx={{ fontSize: '14px', color: theme.palette.custom.gray[600], mb: 1 }}>
              {t('execution.selectAllThatApply')}
            </Box>
            {question.options?.map(option => (
              <FormControlLabel
                key={option.id}
                control={
                  <Checkbox
                    checked={response?.responseText?.split(',').filter(Boolean).includes(option.id?.toString() || '') || false}
                    disabled={isReadOnly}
                    onChange={(e) => {
                      const currentSelections = response?.responseText?.split(',').filter(Boolean) || [];
                      const optionId = option.id?.toString() || '';
                      let newSelections: string[];

                      if (e.target.checked) {
                        newSelections = [...currentSelections, optionId];
                      } else {
                        newSelections = currentSelections.filter(id => id !== optionId);
                      }

                      handleResponseChange(question.id!, question, newSelections.join(','));
                    }}
                  />
                }
                label={option.optionText}
              />
            ))}
          </FormControl>
        );

      case QuestionType.Date:
        return (
          <DatePickerLocalized
            useCalendar={false}
            label={question.questionText || t('execution.selectDate')}
            value={response?.responseText || ''}
            onChange={(newValue) => handleResponseChange(question.id!, question, newValue || '')}
            disabled={isReadOnly}
            translations={{
              clearButtonLabel: t('calendar.clearButtonLabel'),
              todayButtonLabel: t('calendar.todayButtonLabel'),
              cancelButtonLabel: t('calendar.cancelButtonLabel'),
              okButtonLabel: t('calendar.okButtonLabel'),
            }}
          />
        );

      case QuestionType.Rating:
        return (
          <Box>
            <Box sx={{ fontSize: '14px', color: theme.palette.custom.gray[600], mb: 1 }}>
              {t('execution.rateFrom1To5')}
            </Box>
            <FormControl component="fieldset">
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                {[1, 2, 3, 4, 5].map(rating => (
                  <FormControlLabel
                    key={rating}
                    control={
                      <Checkbox
                        checked={response?.responseNumber === rating}
                        disabled={isReadOnly}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleResponseChange(question.id!, question, rating);
                          } else {
                            handleResponseChange(question.id!, question, '');
                          }
                        }}
                      />
                    }
                    label={rating.toString()}
                  />
                ))}
              </Box>
            </FormControl>
          </Box>
        );

      case QuestionType.Photo:
        return (
          <Box>
            <Stack
              direction="row"
              spacing={2}
              mb={0}
            >
              {/* Camera Capture */}
              <Button
                variant="secondary"
                startIcon={<SvgIcon><PhotoCameraIcon /></SvgIcon>}
                disabled={isReadOnly || !canUploadPhotos || (response?.photoUrls?.length || 0) >= 3}
                onClick={() => {
                  setCurrentPhotoQuestionId(question.id!);
                  setIsActionTriggerPhoto(false);
                  setCameraDialogOpen(true);
                }}
              >
                {t('execution.takePhoto')}
              </Button>

              {/* File Selection */}
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id={`photo-select-${question.id}`}
                type="file"
                multiple
                onChange={(e) => {
                  const files = e.target.files;
                  if (files && files.length > 0) {
                    handlePhotoCapture(question.id!, question, files);
                    e.target.value = ''; // Reset input
                  }
                }}
              />
              <label htmlFor={`photo-select-${question.id}`}>
                <Button
                  variant="secondary"
                  component="span"
                  disabled={isReadOnly || !canUploadPhotos || (response?.photoUrls?.length || 0) >= 3}
                >
                  {t('execution.chooseFromDevice')}
                </Button>
              </label>
            </Stack>

            {response?.photoUrls && response.photoUrls.length > 0 && (
              <Box>
                <Box sx={{ fontSize: '14px', color: theme.palette.custom.gray[600], mb: 1 }}>
                  {t('execution.photosOf3', { count: response.photoUrls.length })}
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: 2,
                  }}
                >
                  {response.photoUrls.map((photoUrl, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: 'relative',
                        border: `2px solid ${theme.palette.custom.gray[300]}`,
                        borderRadius: 1,
                        overflow: 'hidden',
                        aspectRatio: '4/3',
                      }}
                    >
                      <img
                        src={photoUrl}
                        alt={`Photo ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                        }}
                      >
                        <Button
                          variant="secondary"
                          startIcon={<SvgIcon><DeleteIcon /></SvgIcon>}
                          onClick={() => handlePhotoRemove(question.id!, index)}
                          disabled={isReadOnly}
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            fontSize: '12px',
                            py: 0.5,
                            px: 1,
                            minWidth: 'auto',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 1)',
                            },
                          }}
                        >
                          {t('common.remove')}
                        </Button>
                      </Box>
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                          color: 'white',
                          fontSize: '12px',
                          py: 0.5,
                          px: 1,
                          textAlign: 'center',
                        }}
                      >
                        {t('execution.photoLabel', { index: index + 1 })}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        );

      case QuestionType.Signature:
        return (
          <Box>
            {response?.signatureUrl && (
              <Box
                mb={2}
                sx={{
                  position: 'relative',
                  display: 'block',
                  border: `2px solid ${theme.palette.custom.gray[300]}`,
                  borderRadius: 1,
                  overflow: 'hidden',
                  backgroundColor: '#fff',
                  width: 'fit-content',
                }}
              >
                <img
                  src={response.signatureUrl}
                  alt={t('execution.signatureAlt')}
                  style={{
                    maxWidth: '100%',
                    maxHeight: 150,
                    display: 'block',
                  }}
                />
                {!isReadOnly && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                    }}
                  >
                    <Button
                      variant="secondary"
                      startIcon={<SvgIcon><DeleteIcon /></SvgIcon>}
                      onClick={() => handleSignatureRemove(question.id!)}
                      disabled={isReadOnly}
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        fontSize: '12px',
                        py: 0.5,
                        px: 1,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                        },
                      }}
                    >
                      {t('common.remove')}
                    </Button>
                  </Box>
                )}
              </Box>
            )}
            {!isReadOnly && canESign && (
              <Button
                variant="secondary"
                onClick={() => {
                  setCurrentSignatureQuestionId(question.id!);
                  setSignatureDialogOpen(true);
                }}
              >
                {response?.signatureUrl ? t('execution.changeSignature') : t('execution.captureSignature')}
              </Button>
            )}
          </Box>
        );

      default:
        return <Box sx={{ color: 'error.main' }}>{t('execution.unsupportedQuestionType')}</Box>;
    }
  };

  if (loading) {
    return (
      <PageArea>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 8,
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <LinearProgress sx={{ width: 200, mb: 2 }} />
            <Box>{t('execution.loadingAudit')}</Box>
          </Box>
        </Box>
      </PageArea>
    );
  }

  if (!audit) {
    return (
      <Box p={3}>
        <Alert severity="error">{t('execution.auditNotFound')}</Alert>
      </Box>
    );
  }

  // Read-only when: user doesn't have execute permission, or audit is submitted
  const isReadOnly = !canExecuteAudit || audit.status === AuditStatus.Submitted;

  // Whether the audit has already been submitted (auditor can still edit responses)
  const isSubmitted = audit.status >= AuditStatus.Submitted;

  // Compute whether all submission requirements are met
  const computeCanSubmit = (): boolean => {
    if (isReadOnly || isSubmitted) return false;
    if (!canESign) return false;

    // All required questions must be answered
    for (const section of sections) {
      for (const question of section.questions || []) {
        if (question.isRequired) {
          const response = responses[question.id!];
          const hasAnswer = response && (
            response.responseText ||
            response.responseNumber !== null ||
            response.responseBoolean !== null ||
            (response.selectedOptionIds && response.selectedOptionIds.length > 0) ||
            (response.photoUrls && response.photoUrls.length > 0) ||
            response.signatureUrl
          );
          if (!hasAnswer) return false;
        }
      }
    }

    // All required action triggers must be fulfilled (note, photo, signature)
    for (const [questionIdStr, triggers] of Object.entries(triggeredActions)) {
      const questionId = parseInt(questionIdStr);
      for (const trigger of triggers) {
        if (trigger.isRequired) {
          const tIdx = triggers.indexOf(trigger);
          const triggerKey = `${questionId}-${tIdx}`;
          if (trigger.triggerType === ActionTriggerType.RequireNote && !triggerNotes[triggerKey]?.trim()) return false;
          if (trigger.triggerType === ActionTriggerType.RequirePhoto && (!triggerPhotos[triggerKey] || triggerPhotos[triggerKey].length === 0)) return false;
          if (trigger.triggerType === ActionTriggerType.RequireSignature && !triggerSignatures[triggerKey]) return false;
          if (trigger.triggerType === ActionTriggerType.CreateActionItem) {
            const draft = actionItemDrafts[triggerKey];
            if (!draft?.title?.trim() || !draft?.description?.trim()) return false;
          }
        }
      }
    }

    return true;
  };
  const canSubmit = computeCanSubmit();

  return (
    <PageArea>
      {/* Header */}
      <Stack
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isMobile ? 'flex-start' : 'center'}
        mb={3}
      >
        <PageTitle subTitle={`${audit.templateName} - ${audit.storeNumber}`}>
          {isReadOnly
            ? t('execution.viewAuditTitle')
            : isSubmitted
              ? t('execution.editAuditTitle')
              : t('execution.pageTitle')}
        </PageTitle>
        {!isReadOnly && (
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={2}
            width={isMobile ? '100%' : 'auto'}
          >
            {!isSubmitted && (
              <Button
                variant="primary"
                size="lg"
                startIcon={<SvgIcon><SendIcon /></SvgIcon>}
                onClick={() => setSubmitDialogOpen(true)}
                disabled={saving || autosaving || !canSubmit}
                fullWidth={isMobile}
              >
                {t('execution.submitAudit')}
              </Button>
            )}
          </Stack>
        )}
      </Stack>

      {/* Autosave snackbar */}
      <MessagesSnackbar
        open={showAutosaveMessage}
        onClose={() => setShowAutosaveMessage(false)}
        message={t('execution.changesSaved')}
        severity="success"
        duration={3000}
      />

      {/* Alerts */}
      {uploading && (
        <Alert severity="info"
          icon={<CircularProgress size={20} />}
          sx={{ mb: 2 }}>
          {t('execution.uploadingPhoto', { defaultValue: 'Uploading...' })}
        </Alert>
      )}
      {error && (
        <Alert severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success"
          onClose={() => setSuccess(null)}
          sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {(() => {
        const totalQuestions = sections.reduce(
          (sum, s) => sum + (s.questions?.length || 0),
          0,
        );
        const answeredQuestions = sections.reduce((sum, s) => {
          return sum + (s.questions?.filter((q) => {
            const r = responses[q.id!];
            if (!r) return false;
            return (
              r.responseText ||
              r.responseNumber !== null ||
              r.responseBoolean !== null ||
              (r.selectedOptionIds && r.selectedOptionIds.length > 0) ||
              (r.photoUrls && r.photoUrls.length > 0) ||
              r.signatureUrl
            );
          }).length || 0);
        }, 0);
        const progressPercent = totalQuestions > 0
          ? Math.round((answeredQuestions / totalQuestions) * 100)
          : 0;

        return (
          <Box sx={{ mb: 5 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Box sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.custom.gray[700] }}>
                {t('execution.progress', {
                  answered: answeredQuestions,
                  total: totalQuestions,
                  defaultValue: `${answeredQuestions} / ${totalQuestions} questions answered`,
                })}
              </Box>
              <Box sx={{ fontSize: '14px', fontWeight: 700, color: theme.palette.primary.main }}>
                {progressPercent}%
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: theme.palette.custom.gray[200],
                '& .MuiLinearProgress-bar': {
                  borderRadius: 5,
                },
              }}
            />
          </Box>
        );
      })()}

      {sections.length > 1 && (
        <Box sx={{ mb: 5 }}>
          <Stepper
            activeStep={currentSectionIndex}
            alternativeLabel
          >
            {sections.map((section, idx) => (
              <Step
                key={section.id}
                completed={idx !== currentSectionIndex && isSectionComplete(section)}
              >
                <StepLabel
                  error={idx !== currentSectionIndex && !isSectionComplete(section) && idx < currentSectionIndex}
                >
                  {section.name}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      )}

      {sections[currentSectionIndex] && (() => {
        const section = sections[currentSectionIndex];
        const sectionIndex = currentSectionIndex;
        return (
          <Section
            key={section.id}
            style={{
              p: 5, mb: 5,
            }}
          >
            <Box sx={{ mb: 5 }}>
              <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.5 }}>
                {t('execution.sectionLabel', { index: sectionIndex + 1, name: section.name, defaultValue: `Section ${sectionIndex + 1}: ${section.name}` })}
              </Box>
              {section.description && (
                <Box sx={{ fontSize: '14px', color: theme.palette.custom.gray[600], mb: 2 }}>
                  {section.description}
                </Box>
              )}
            </Box>

            {/* Questions */}
            <Stack spacing={3}>
              {section.questions?.map((question, index) => (
                <Section style={{ p: 5, mb: 0, boxShadow: 'none' }}
                  key={question.id}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="start"
                    mb={5}
                  >
                    <Box sx={{ fontSize: '16px', fontWeight: 600, color: theme.palette.custom.gray[900] }}>
                      {index + 1}. {question.questionText}
                      {question.isRequired && <span style={{ color: 'red' }}> *</span>}
                    </Box>
                  </Box>
                  {question.helpText && (
                    <Box sx={{ fontSize: '13px', color: theme.palette.custom.gray[600], mb: 1 }}>
                      {question.helpText}
                    </Box>
                  )}
                  {renderQuestion(question)}

                  {/* Action Trigger Alerts */}
                  {triggeredActions[question.id!] && triggeredActions[question.id!].map((trigger, triggerIdx) => (
                    <Box key={triggerIdx}>
                      <Alert
                        severity={trigger.isRequired ? 'warning' : 'info'}
                        sx={{ mt: 2, '& .MuiAlert-message': { width: '100%' } }}
                      >
                        <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                          {trigger.triggerType === ActionTriggerType.RequireNote && t('execution.noteRequired')}
                          {trigger.triggerType === ActionTriggerType.RequirePhoto && t('execution.photoRequired')}
                          {trigger.triggerType === ActionTriggerType.CreateActionItem && t('execution.actionItemWillBeCreated')}
                          {trigger.triggerType === ActionTriggerType.RequireSignature && t('execution.signatureRequired')}
                        </Box>
                        <Box sx={{ mb: 1 }}>{trigger.promptText}</Box>

                        {/* Action Photo Capture */}
                        {trigger.triggerType === ActionTriggerType.RequirePhoto && (
                          <Box sx={{ mt: 1 }}>
                            <Stack direction="row"
                              spacing={1}
                              flexWrap="wrap">
                              <Button
                                variant="secondary"
                                size="md"
                                startIcon={<PhotoCameraIcon />}
                                disabled={isReadOnly}
                                onClick={() => {
                                  setCurrentPhotoQuestionId(question.id!);
                                  setCurrentPhotoTriggerIdx(triggerIdx);
                                  setIsActionTriggerPhoto(true);
                                  setCameraDialogOpen(true);
                                }}
                              >
                                {t('execution.takePhoto')}
                              </Button>
                              <Button
                                variant="secondary"
                                size="md"
                                component="label"
                                disabled={isReadOnly}
                              >
                                {t('execution.uploadPhoto')}
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  hidden
                                  onChange={async (e) => {
                                    if (e.target.files && e.target.files.length > 0) {
                                      const photoKey = `${question.id!}-${triggerIdx}`;
                                      const existingPhotos = triggerPhotos[photoKey] || [];
                                      const filesToAdd = Array.from(e.target.files).slice(0, 3 - existingPhotos.length);
                                      try {
                                        setUploading(true);
                                        const uploadPromises = filesToAdd.map(file =>
                                          uploadToBlob(question.id!, file),
                                        );
                                        const uploadedUrls = await Promise.all(uploadPromises);
                                        const allPhotoUrls = [...existingPhotos, ...uploadedUrls];
                                        const updatedPhotos = { ...triggerPhotos, [photoKey]: allPhotoUrls };
                                        setTriggerPhotos(updatedPhotos);
                                        syncTriggerMediaToResponse(question.id!, updatedPhotos, triggerSignatures);
                                      } catch {
                                        setError(t('execution.photoProcessFailed'));
                                        setTimeout(() => setError(null), 3000);
                                      } finally {
                                        setUploading(false);
                                      }
                                    }
                                  }}
                                />
                              </Button>
                            </Stack>

                            {/* Display Action Photos for this trigger */}
                            {(triggerPhotos[`${question.id!}-${triggerIdx}`] || []).length > 0 && (
                              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                {(triggerPhotos[`${question.id!}-${triggerIdx}`] || []).map((photoUrl, photoIdx) => (
                                  <Box
                                    key={photoIdx}
                                    sx={{
                                      position: 'relative',
                                      width: 100,
                                      height: 100,
                                      border: 1,
                                      borderColor: 'divider',
                                      borderRadius: 1,
                                      overflow: 'hidden',
                                    }}
                                  >
                                    <img
                                      src={photoUrl}
                                      alt={t('execution.actionPhotoAlt', { index: photoIdx + 1 })}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                      }}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const photoKey = `${question.id!}-${triggerIdx}`;
                                        const newPhotoUrls = [...(triggerPhotos[photoKey] || [])];
                                        newPhotoUrls.splice(photoIdx, 1);
                                        const updatedPhotos = { ...triggerPhotos, [photoKey]: newPhotoUrls };
                                        setTriggerPhotos(updatedPhotos);
                                        syncTriggerMediaToResponse(question.id!, updatedPhotos, triggerSignatures);
                                      }}
                                      sx={{
                                        position: 'absolute',
                                        top: 2,
                                        right: 2,
                                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                                        '&:hover': { bgcolor: 'white' },
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        )}

                        {/* Action Item - must fill in details */}
                        {trigger.triggerType === ActionTriggerType.CreateActionItem && (
                          <Box sx={{ mt: 1 }}>
                            {actionItemDrafts[`${question.id!}-${triggerIdx}`]?.title ? (
                              <Button
                                variant="secondary"
                                disabled={isReadOnly}
                                startIcon={<AssignmentIcon />}
                                onClick={() => openActionItemDialog(question.id!, triggerIdx, trigger)}
                              >
                                {t('execution.actionItemEditDetails')}
                              </Button>
                            ) : (
                              <Button
                                variant="secondary"
                                size="md"
                                startIcon={<AssignmentIcon />}
                                disabled={isReadOnly}
                                onClick={() => openActionItemDialog(question.id!, triggerIdx, trigger)}
                              >
                                {t('execution.actionItemDetails')}
                              </Button>
                            )}
                          </Box>
                        )}

                        {/* Action Signature Capture */}
                        {trigger.triggerType === ActionTriggerType.RequireSignature && (
                          <Box sx={{ mt: 1 }}>
                            {triggerSignatures[`${question.id!}-${triggerIdx}`] ? (
                              <Box>
                                <Box
                                  sx={{
                                    border: `1px solid ${theme.palette.custom.gray[300]}`,
                                    borderRadius: '8px',
                                    p: 1,
                                    backgroundColor: '#fff',
                                    display: 'inline-block',
                                  }}
                                >
                                  <img
                                    src={triggerSignatures[`${question.id!}-${triggerIdx}`]}
                                    alt={t('execution.signatureAlt')}
                                    style={{ maxWidth: '100%', maxHeight: 120, display: 'block' }}
                                  />
                                </Box>
                                {!isReadOnly && (
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{ mt: 1 }}
                                  >
                                    <Button
                                      variant="secondary"
                                      size="md"
                                      onClick={() => {
                                        setCurrentSignatureQuestionId(question.id!);
                                        setCurrentSignatureTriggerIdx(triggerIdx);
                                        setIsActionTriggerSignature(true);
                                        setSignatureDialogOpen(true);
                                      }}
                                    >
                                      {t('execution.changeSignature')}
                                    </Button>
                                    <Button
                                      variant="secondary"
                                      size="md"
                                      startIcon={<SvgIcon><DeleteIcon /></SvgIcon>}
                                      onClick={() => {
                                        const sigKey = `${question.id!}-${triggerIdx}`;
                                        const updatedSigs = { ...triggerSignatures };
                                        delete updatedSigs[sigKey];
                                        setTriggerSignatures(updatedSigs);
                                        syncTriggerMediaToResponse(question.id!, triggerPhotos, updatedSigs);
                                      }}
                                    >
                                      {t('execution.removeSignature')}
                                    </Button>
                                  </Stack>
                                )}
                              </Box>
                            ) : (
                              <Button
                                variant="secondary"
                                size="md"
                                disabled={isReadOnly}
                                onClick={() => {
                                  setCurrentSignatureQuestionId(question.id!);
                                  setCurrentSignatureTriggerIdx(triggerIdx);
                                  setIsActionTriggerSignature(true);
                                  setSignatureDialogOpen(true);
                                }}
                              >
                                {t('execution.captureSignature')}
                              </Button>
                            )}
                          </Box>
                        )}
                      </Alert>

                      {trigger.triggerType === ActionTriggerType.RequireNote && (
                        <TextField
                          fullWidth
                          multiline
                          minRows={6}
                          maxRows={20}
                          placeholder={t('execution.enterNotesHere')}
                          value={triggerNotes[`${question.id!}-${triggerIdx}`] || ''}
                          onChange={(e) => {
                            const noteKey = `${question.id!}-${triggerIdx}`;
                            const updatedNotes = { ...triggerNotes, [noteKey]: e.target.value };
                            setTriggerNotes(updatedNotes);
                            // Concatenate all RequireNote trigger notes for this question into actionNote
                            const allTriggers = triggeredActions[question.id!] || [];
                            const combinedNote = allTriggers
                              .map((t, idx) => t.triggerType === ActionTriggerType.RequireNote ? updatedNotes[`${question.id!}-${idx}`] || '' : null)
                              .filter((n): n is string => n !== null)
                              .join('\n---\n');
                            const response = responses[question.id!];
                            const newResponse = { ...response, actionNote: combinedNote };
                            setResponses({ ...responses, [question.id!]: newResponse });
                            setModifiedQuestions(prev => new Set(prev).add(question.id!));
                            setShowAutosaveMessage(false);
                            debouncedSave();
                          }}
                          sx={{ mt: 1, bgcolor: 'white' }}
                          required={trigger.isRequired}
                          disabled={isReadOnly}
                        />
                      )}
                    </Box>
                  ))}
                </Section>
              ))}
            </Stack>
          </Section>
        );
      })()}

      {sections.length > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, mb: 3 }}>
          <Button
            variant="secondary"
            size="lg"
            onClick={() => {
              setCurrentSectionIndex(currentSectionIndex - 1);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            disabled={currentSectionIndex === 0}
          >
            {t('execution.previousSection', { defaultValue: 'Previous Section' })}
          </Button>
          <Button
            variant="secondary"
            size="lg"
            onClick={() => {
              setCurrentSectionIndex(currentSectionIndex + 1);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            disabled={currentSectionIndex === sections.length - 1}
          >
            {t('execution.nextSection', { defaultValue: 'Next Section' })}
          </Button>
        </Box>
      )}

      {!isReadOnly && !isSubmitted && (
        <Stack
          direction="row"
          justifyContent="flex-end"
          sx={{ mt: 3 }}
        >
          <Button
            variant="primary"
            size="lg"
            startIcon={<SvgIcon><SendIcon /></SvgIcon>}
            onClick={() => setSubmitDialogOpen(true)}
            disabled={saving || autosaving || !canSubmit}
          >
            {t('execution.submitAudit')}
          </Button>
        </Stack>
      )}

      {/* Action Item Details Dialog */}
      <Dialog
        open={actionItemDialogOpen}
        onClose={() => {
          setActionItemDialogOpen(false);
          setCurrentActionItemKey(null);
          setCurrentActionItemTrigger(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('execution.actionItemDetails')}</DialogTitle>
        <DialogContent>
          {currentActionItemKey && actionItemDrafts[currentActionItemKey] && (
            <Stack
              spacing={4}
              sx={{ mt: 2 }}
            >
              {currentActionItemTrigger?.promptText && (
                <Alert severity="info">
                  {currentActionItemTrigger.promptText}
                </Alert>
              )}
              <TextField
                label={t('execution.actionItemTitle')}
                placeholder={t('execution.actionItemTitlePlaceholder')}
                value={actionItemDrafts[currentActionItemKey].title || ''}
                onChange={(e) => setActionItemDrafts(prev => ({
                  ...prev,
                  [currentActionItemKey!]: { ...prev[currentActionItemKey!], title: e.target.value },
                }))}
                fullWidth
                required
              />
              <TextField
                label={t('execution.actionItemDescription')}
                placeholder={t('execution.actionItemDescriptionPlaceholder')}
                value={actionItemDrafts[currentActionItemKey].description || ''}
                onChange={(e) => setActionItemDrafts(prev => ({
                  ...prev,
                  [currentActionItemKey!]: { ...prev[currentActionItemKey!], description: e.target.value },
                }))}
                fullWidth
                multiline
                rows={3}
                required
              />
              <TextField
                label={t('execution.actionItemPriority')}
                value={actionItemDrafts[currentActionItemKey].priority || 'Medium'}
                onChange={(e) => setActionItemDrafts(prev => ({
                  ...prev,
                  [currentActionItemKey!]: { ...prev[currentActionItemKey!], priority: e.target.value as ActionItemDetails['priority'] },
                }))}
                select
                fullWidth
              >
                <MenuItem value="Low">{t('common.low')}</MenuItem>
                <MenuItem value="Medium">{t('common.medium')}</MenuItem>
                <MenuItem value="High">{t('common.high')}</MenuItem>
                <MenuItem value="Critical">{t('common.critical')}</MenuItem>
              </TextField>
              <DatePickerLocalized
                label={t('execution.actionItemDueDate')}
                value={actionItemDrafts[currentActionItemKey].dueDate || null}
                useCalendar={false}
                onChange={(date: string | null) => setActionItemDrafts(prev => ({
                  ...prev,
                  [currentActionItemKey!]: { ...prev[currentActionItemKey!], dueDate: date },
                }))}
                translations={{
                  clearButtonLabel: t('calendar.clearButtonLabel'),
                  todayButtonLabel: t('calendar.todayButtonLabel'),
                  cancelButtonLabel: t('calendar.cancelButtonLabel'),
                  okButtonLabel: t('calendar.okButtonLabel'),
                }}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setActionItemDialogOpen(false);
              setCurrentActionItemKey(null);
              setCurrentActionItemTrigger(null);
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={saveActionItemDraft}
            disabled={
              !currentActionItemKey
              || !actionItemDrafts[currentActionItemKey!]?.title?.trim()
              || !actionItemDrafts[currentActionItemKey!]?.description?.trim()
            }
          >
            {t('execution.actionItemSaveDetails')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={submitDialogOpen}
        onClose={() => setSubmitDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('execution.submitDialogTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ fontSize: '16px', color: theme.palette.custom.gray[700], mb: 2 }}>
            {t('execution.submitDialogMessage')}
          </Box>

          {/* Display Auditor Name (read-only) */}
          <TextField
            fullWidth
            label={t('execution.auditor')}
            value={auditorName}
            disabled
            sx={{ mb: 3 }}
            helperText={t('execution.auditorHelperText')}
          />

          {/* Signature Section */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ fontSize: '14px', fontWeight: 500, color: theme.palette.custom.gray[700], mb: 1 }}>
              {t('execution.signatureLabel')}
            </Box>
            {auditorSignature ? (
              <Box>
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.custom.gray[300]}`,
                    borderRadius: '8px',
                    p: 2,
                    backgroundColor: theme.palette.custom.gray[50],
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '150px',
                  }}
                >
                  <img
                    src={auditorSignature}
                    alt={t('execution.signatureAlt')}
                    style={{ maxWidth: '100%', maxHeight: '140px' }}
                  />
                </Box>
                <Button
                  variant="secondary"
                  onClick={() => setAuditorSignature('')}
                  sx={{ mt: 1 }}
                >
                  {t('execution.clearSignature')}
                </Button>
              </Box>
            ) : (
              <Box>
                <Box
                  sx={{
                    border: `2px dashed ${theme.palette.custom.gray[300]}`,
                    borderRadius: '8px',
                    p: 3,
                    backgroundColor: theme.palette.custom.gray[50],
                    textAlign: 'center',
                    minHeight: '150px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ fontSize: '14px', color: theme.palette.custom.gray[500], mb: 2 }}>
                    {t('execution.noSignatureYet')}
                  </Box>
                  <Button
                    variant="primary"
                    onClick={() => setSignatureDialogOpen(true)}
                  >
                    {t('execution.signHere')}
                  </Button>
                </Box>
              </Box>
            )}
            <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600], mt: 1 }}>
              {t('execution.signatureDisclaimer')}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            onClick={() => setSubmitDialogOpen(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={saving || autosaving || !auditorSignature}
          >
            {t('common.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Camera Capture Dialog */}
      <CameraCaptureDialog
        open={cameraDialogOpen}
        onClose={() => {
          setCameraDialogOpen(false);
          setCurrentPhotoQuestionId(null);
          setCurrentPhotoTriggerIdx(null);
          setIsActionTriggerPhoto(false);
        }}
        onCapture={async (photoDataUrl: string) => {
          if (currentPhotoQuestionId) {
            if (isActionTriggerPhoto && currentPhotoTriggerIdx !== null) {
              try {
                setUploading(true);
                const uploadedUrl = await uploadToBlob(currentPhotoQuestionId, photoDataUrl);
                const photoKey = `${currentPhotoQuestionId}-${currentPhotoTriggerIdx}`;
                const existingPhotos = triggerPhotos[photoKey] || [];
                const allPhotoUrls = [...existingPhotos, uploadedUrl];
                const updatedPhotos = { ...triggerPhotos, [photoKey]: allPhotoUrls };
                setTriggerPhotos(updatedPhotos);
                syncTriggerMediaToResponse(currentPhotoQuestionId, updatedPhotos, triggerSignatures);
              } catch {
                setError(t('execution.photoProcessFailed'));
                setTimeout(() => setError(null), 3000);
              } finally {
                setUploading(false);
              }
            } else {
              // Regular question photo: store in photoUrls
              const question = sections
                .flatMap(s => s.questions || [])
                .find(q => q.id === currentPhotoQuestionId);
              if (question) {
                handleCameraCapture(currentPhotoQuestionId, question, photoDataUrl);
              }
            }
          }
          setCameraDialogOpen(false);
          setCurrentPhotoTriggerIdx(null);
          setIsActionTriggerPhoto(false);
        }}
      />

      {/* Signature Capture Dialog */}
      <SignaturePadDialog
        open={signatureDialogOpen}
        onClose={() => {
          setSignatureDialogOpen(false);
          setCurrentSignatureQuestionId(null);
          setCurrentSignatureTriggerIdx(null);
          setIsActionTriggerSignature(false);
        }}
        onSave={async (signatureDataUrl) => {
          if (currentSignatureQuestionId && isActionTriggerSignature && currentSignatureTriggerIdx !== null) {
            try {
              setUploading(true);
              const uploadedUrl = await uploadToBlob(currentSignatureQuestionId, signatureDataUrl);
              const sigKey = `${currentSignatureQuestionId}-${currentSignatureTriggerIdx}`;
              const updatedSigs = { ...triggerSignatures, [sigKey]: uploadedUrl };
              setTriggerSignatures(updatedSigs);
              syncTriggerMediaToResponse(currentSignatureQuestionId, triggerPhotos, updatedSigs);
            } catch {
              setError(t('execution.photoProcessFailed'));
              setTimeout(() => setError(null), 3000);
            } finally {
              setUploading(false);
            }
          } else if (currentSignatureQuestionId) {
            // Question-type signature
            const question = sections
              .flatMap(s => s.questions || [])
              .find(q => q.id === currentSignatureQuestionId);
            if (question) {
              handleSignatureCapture(currentSignatureQuestionId, question, signatureDataUrl);
            }
          } else {
            try {
              setUploading(true);
              const uploadedUrl = await uploadToBlob(0, signatureDataUrl);
              setAuditorSignature(uploadedUrl);
            } catch {
              setAuditorSignature(signatureDataUrl);
            } finally {
              setUploading(false);
            }
          }
          setSignatureDialogOpen(false);
          setCurrentSignatureQuestionId(null);
          setCurrentSignatureTriggerIdx(null);
          setIsActionTriggerSignature(false);
        }}
      />

      {/* Leave Page Modal — save prompt on navigation with unsaved changes */}
      <LeavePageModal
        open={blocker.state === 'blocked' && modifiedQuestions.size > 0}
        onConfirm={handleLeavePageModalConfirm}
        onClose={handleLeavePageModalClose}
        onCancel={handleLeavePageModalCancel}
        onConfirmUrl='/compliance/audits'
        leaveWarningContent={t('execution.saveOnLeaveWarningContent')}
        leaveWarningTitle={t('execution.saveOnLeaveWarningTitle')}
      />
    </PageArea>
  );
};

// Camera Capture Dialog Component
interface CameraCaptureDialogProps {
  open: boolean;
  onClose: () => void;
  onCapture: (_photoDataUrl: string) => void;
}

const CameraCaptureDialog: React.FC<CameraCaptureDialogProps> = ({ open, onClose, onCapture }) => {
  const theme = useTheme();
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = React.useState<MediaStream | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isCameraReady, setIsCameraReady] = React.useState(false);
  const { t } = useTranslation('complianceAudits');

  React.useEffect(() => {
    if (open) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [open]);

  const startCamera = async () => {
    try {
      setError(null);
      setIsCameraReady(false);

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }, // Prefer back camera on mobile
        audio: false,
      });

      setStream(mediaStream);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play();
          setIsCameraReady(true);
        };
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError(t('execution.cameraAccessFailed'));
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsCameraReady(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the current video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to data URL
    const photoDataUrl = canvas.toDataURL('image/jpeg', 0.9);

    // Call the onCapture callback
    onCapture(photoDataUrl);

    // Stop camera and close dialog
    stopCamera();
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{t('execution.takePhotoDialog')}</DialogTitle>
      <DialogContent>
        {error ? (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
          >
            {error}
          </Alert>
        ) : (
          <Box sx={{ mb: 2, fontSize: '14px', color: theme.palette.custom.gray[600] }}>
            {t('execution.takePhotoInstructions')}
          </Box>
        )}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            backgroundColor: '#000',
            borderRadius: 1,
            overflow: 'hidden',
            aspectRatio: '4/3',
          }}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: isCameraReady ? 'block' : 'none',
            }}
          />
          {!isCameraReady && !error && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'white',
                textAlign: 'center',
              }}
            >
              <CircularProgress sx={{ color: 'white', mb: 2 }} />
              <Box>{t('execution.startingCamera')}</Box>
            </Box>
          )}
        </Box>
        {/* Hidden canvas for capturing the photo */}
        <canvas
          ref={canvasRef}
          style={{ display: 'none' }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          variant="secondary"
          onClick={handleClose}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={capturePhoto}
          disabled={!isCameraReady}
          startIcon={<SvgIcon><PhotoCameraIcon /></SvgIcon>}
        >
          {t('execution.capturePhoto')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Signature Pad Dialog Component
interface SignaturePadDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (_signatureDataUrl: string) => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SignaturePadDialog: React.FC<SignaturePadDialogProps> = ({ open, onClose, onSave }) => {
  const theme = useTheme();
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = React.useState(false);
  const [isEmpty, setIsEmpty] = React.useState(true);
  const { t } = useTranslation('complianceAudits');

  React.useEffect(() => {
    if (open && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Clear canvas with white background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        // Set drawing styles
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }
      setIsEmpty(true);
    }
  }, [open]);

  const getCoordinates = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if ('touches' in e) {
      const touch = e.touches[0];
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      };
    } else {
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    }
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setIsEmpty(false);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas || isEmpty) return;

    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{t('execution.captureSignatureDialog')}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, fontSize: '14px', color: theme.palette.custom.gray[600] }}>
          {t('execution.captureSignatureInstructions')}
        </Box>
        <Box
          sx={{
            border: `2px solid ${theme.palette.custom.gray[300]}`,
            borderRadius: 1,
            overflow: 'hidden',
            backgroundColor: '#ffffff',
            touchAction: 'none',
            cursor: 'crosshair',
          }}
        >
          <canvas
            ref={canvasRef}
            width={600}
            height={300}
            style={{
              display: 'block',
              width: '100%',
              height: 'auto',
            }}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="secondary"
          onClick={clearSignature}
        >
          {t('execution.clear')}
        </Button>
        <Button
          variant="secondary"
          onClick={onClose}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={saveSignature}
          disabled={isEmpty}
        >
          {t('execution.saveSignature')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuditExecutionPage;

